import Editor, { OnMount } from '@monaco-editor/react'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import {
	Box,
	FormControl,
	IconButton,
	InputLabel,
	OutlinedInput,
	Tooltip,
} from '@mui/material'
import type { editor } from 'monaco-editor'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { useAppContext } from '@/hooks'
import { APP_FONT_FAMILY } from '@/styles/theme'
import { registerSqlAutocomplete } from '@/utils/monaco/sqlAutocomplete'

import {
	EditorLanguage,
	FullscreenEditorModal,
} from '../MrtTable/components/FullscreenEditorModal'

/** Approximate line height in Monaco (fontSize 13 + padding). Empty = 2 rows, filled = 10 rows. */
const ROW_HEIGHT = 20
const EMPTY_EDITOR_ROWS = 2
const FILLED_EDITOR_ROWS = 10
/** Extra px below content so there is a small gap. */
const CONTENT_HEIGHT_GAP = 12
/** Right padding so content does not run under the fullscreen button (MDD-2601). */
const FULLSCREEN_BUTTON_RIGHT_PADDING = 40

interface MonacoEditorFieldProps {
	disabled?: boolean
	/** Element/row identity shown next to the attribute in the fullscreen header */
	elementLabel?: string
	/** Override computed height (optional). Otherwise: 2 rows when empty, 10 rows when filled. */
	height?: number | string
	/** When true, enable SQL script autocomplete (keywords, functions, snippets). Use for criteria script etc. */
	isSqlScript?: boolean
	language?: EditorLanguage
	onChange?: (value: string) => void
	/** When true and disabled, show parsed Markdown instead of read-only Monaco (e.g. for description/comment). */
	renderMarkdownWhenReadOnly?: boolean
	title: string
	value?: string
}

/**
 * Monaco Editor Field component for standalone use (outside tables)
 *
 * Features:
 * - Monaco editor with syntax highlighting
 * - Fullscreen mode with F11
 * - Ctrl+S to save
 * - Read-only mode support
 * - When renderMarkdownWhenReadOnly and disabled, shows parsed Markdown (e.g. description/comment)
 */
const emptyHeight = EMPTY_EDITOR_ROWS * ROW_HEIGHT + CONTENT_HEIGHT_GAP
const filledHeight = FILLED_EDITOR_ROWS * ROW_HEIGHT
/** Min height so fullscreen button (top: 8) has space below and doesn't touch bottom border in initial state. */
const MIN_WRAPPER_HEIGHT = 60
/** Default height for read-only markdown (10 rows ≈ 212px) so more content is visible without resizing. */
const READONLY_DEFAULT_ROWS = 10
const readOnlyDefaultHeight =
	READONLY_DEFAULT_ROWS * ROW_HEIGHT + CONTENT_HEIGHT_GAP

/** Styles for parsed Markdown in read-only mode (matches ViewDetailV2 description/comment look). */
const readOnlyMarkdownSx = {
	fontSize: '0.875rem',
	lineHeight: 1.5,
	fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
	'& p, & li, & td, & th': { fontFamily: 'inherit', fontSize: '0.875rem' },
	'& em': { fontStyle: 'italic' },
	'& strong': { fontWeight: 600 },
	'& h1': {
		fontSize: '1.125rem',
		fontWeight: 600,
		marginTop: '0.5rem',
		marginBottom: '0.25rem',
		paddingBottom: '0.2em',
		borderBottom: '1px solid rgba(0,0,0,0.12)',
	},
	'& h2': {
		fontSize: '1.0625rem',
		fontWeight: 600,
		marginTop: '0.4rem',
		marginBottom: '0.2rem',
		paddingBottom: '0.2em',
		borderBottom: '1px solid rgba(0,0,0,0.08)',
	},
	'& h3': {
		fontSize: '1rem',
		fontWeight: 600,
		marginTop: '0.35rem',
		marginBottom: '0.15rem',
	},
	'& h4, & h5, & h6': {
		fontSize: '0.9375rem',
		fontWeight: 600,
		marginTop: '0.25rem',
		marginBottom: '0.15rem',
	},
	'& code': {
		fontFamily: '"Roboto Mono", "Consolas", "Monaco", monospace',
		fontSize: '0.8125rem',
	},
	'& table': {
		borderCollapse: 'collapse',
		width: '100%',
		display: 'table',
		marginTop: '0.5rem',
		marginBottom: '0.5rem',
	},
	'& th, & td': {
		border: '1px solid rgba(0, 0, 0, 0.23)',
		padding: '6px 10px',
		textAlign: 'left',
		display: 'table-cell',
	},
	'& th': { backgroundColor: 'action.hover', fontWeight: 600 },
	'& tr': { display: 'table-row' },
	'& thead': { display: 'table-header-group' },
	'& tbody': { display: 'table-row-group' },
}

/** Custom react-markdown components (no extra packages): links open in new tab, block code styled, images constrained. */
const readOnlyMarkdownComponents: React.ComponentProps<
	typeof ReactMarkdown
>['components'] = {
	a: ({ href, children, ...props }) => (
		<a href={href} target="_blank" rel="noopener noreferrer" {...props}>
			{children}
		</a>
	),
	code: ({ className, children, ...props }) => {
		const isBlock = Boolean(className)
		if (isBlock) {
			return (
				<pre
					style={{
						margin: '0.5rem 0',
						padding: '10px 12px',
						overflow: 'auto',
						background: 'rgba(0,0,0,0.06)',
						borderRadius: '4px',
						fontSize: '0.8125rem',
						fontFamily: '"Roboto Mono", "Consolas", "Monaco", monospace',
					}}
				>
					<code className={className} {...props}>
						{children}
					</code>
				</pre>
			)
		}
		return (
			<code className={className} {...props}>
				{children}
			</code>
		)
	},
	img: ({ src, alt, ...props }) => (
		<img
			src={src}
			alt={alt ?? ''}
			loading="lazy"
			style={{ maxWidth: '100%', height: 'auto' }}
			{...props}
		/>
	),
}

/** Custom input for read-only markdown: fills container and scrolls when content overflows. */
const MarkdownReadOnlyInput = React.forwardRef<
	HTMLDivElement,
	{ value?: string } & Record<string, unknown>
>(function MarkdownReadOnlyInput({ value = '', ...other }, ref) {
	return (
		<Box
			ref={ref}
			component="div"
			sx={{
				height: '100%',
				minHeight: emptyHeight,
				overflow: 'auto',
				pr: `${14 + FULLSCREEN_BUTTON_RIGHT_PADDING}px`,
				pb: '8.5px',
				pl: '14px',
				boxSizing: 'border-box',
				width: '100%',
				display: 'block',
				...readOnlyMarkdownSx,
			}}
			{...other}
		>
			{value ? (
				<ReactMarkdown
					remarkPlugins={[remarkGfm]}
					components={readOnlyMarkdownComponents}
				>
					{value}
				</ReactMarkdown>
			) : (
				<span style={{ color: 'rgba(0, 0, 0, 0.6)' }}> </span>
			)}
		</Box>
	)
})

/** Rows to show from content (2–10). Empty = 2; when user adds lines, show lineCount+1 so e.g. 2nd row has size 3 (MDD-2589). */
const getContentRows = (text: string): number => {
	const t = (text ?? '').trim()
	if (!t) return EMPTY_EDITOR_ROWS
	const lines = t.split('\n').length
	return Math.min(FILLED_EDITOR_ROWS, Math.max(EMPTY_EDITOR_ROWS, lines + 1))
}

/** Focusable selector (same as browser tab order). */
const FOCUSABLE_SELECTOR =
	'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Move focus to next (forward) or previous (back) focusable element in the document. */
function moveFocusToAdjacent(back: boolean): void {
	const focusables = [
		...document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
	].filter((el) => el.offsetParent !== null)
	const current = document.activeElement
	if (!current || focusables.length === 0) return
	const currentIndex = focusables.findIndex(
		(el) => el === current || el.contains(current as Node),
	)
	if (currentIndex === -1) return
	const nextIndex = back ? currentIndex - 1 : currentIndex + 1
	const next = focusables[nextIndex]
	if (next) next.focus()
}

interface MonacoInputProps {
	boxHeight: number
	disabled: boolean
	isSqlScript: boolean
	language: EditorLanguage
	onChange: (value: string) => void
	onMount: OnMount
	value: string
}

const MonacoEditorInput = React.forwardRef<HTMLDivElement, MonacoInputProps>(
	function MonacoEditorInput(
		{
			value,
			onChange,
			language,
			disabled,
			boxHeight: _boxHeight,
			onMount,
			isSqlScript,
		},
		ref,
	) {
		const handleChange = useCallback(
			(v: string | undefined) => onChange(v ?? ''),
			[onChange],
		)
		return (
			<Box
				ref={ref}
				sx={{
					width: '100%',
					height: '100%',
					minHeight: Math.max(emptyHeight, MIN_WRAPPER_HEIGHT),
					overflow: 'hidden',
					boxSizing: 'border-box',
					pt: '4px',
				}}
			>
				<Editor
					height="100%"
					language={language}
					value={value}
					onChange={handleChange}
					onMount={onMount}
					options={{
						readOnly: disabled,
						domReadOnly: disabled,
						minimap: { enabled: false },
						fontSize: 13,
						fontFamily: APP_FONT_FAMILY,
						lineNumbers: isSqlScript ? 'on' : 'off',
						wordWrap: 'on',
						automaticLayout: true,
						scrollBeyondLastLine: false,
						tabSize: 2,
						theme: 'vs',
						scrollbar: { vertical: 'auto', horizontal: 'auto' },
						overviewRulerLanes: 0,
						hideCursorInOverviewRuler: true,
						overviewRulerBorder: false,
						renderLineHighlight: 'none',
						glyphMargin: false,
						folding: false,
						lineDecorationsWidth: 8,
						lineNumbersMinChars: 3,
						...(disabled && {
							cursorStyle: 'line',
							cursorBlinking: 'solid',
						}),
					}}
				/>
			</Box>
		)
	},
)

export const MonacoEditorField = ({
	title,
	elementLabel,
	value = '',
	onChange,
	disabled = false,
	language = 'sql',
	isSqlScript = false,
	height: heightProp,
	renderMarkdownWhenReadOnly = false,
}: MonacoEditorFieldProps) => {
	const { t } = useAppContext()
	const [editorValue, setEditorValue] = useState(value)
	const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)
	const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)

	const contentRows = getContentRows(editorValue ?? value)
	const contentHeight =
		typeof heightProp === 'number'
			? heightProp
			: contentRows * ROW_HEIGHT + CONTENT_HEIGHT_GAP
	const minHeightFromContent = contentRows * ROW_HEIGHT + CONTENT_HEIGHT_GAP
	const [boxHeight, setBoxHeight] = useState(() =>
		Math.max(
			emptyHeight,
			Math.min(filledHeight + CONTENT_HEIGHT_GAP, contentHeight),
		),
	)
	const [readOnlyBoxHeight, setReadOnlyBoxHeight] = useState<number | null>(
		null,
	)

	// Sync editor value when parent resets (e.g. after save/cancel)
	useEffect(() => {
		setEditorValue(value)
	}, [value])

	// Keep height in sync with content (2–10 rows); grow when content needs more space
	useEffect(() => {
		setBoxHeight((prev) => Math.max(prev, contentHeight))
	}, [contentHeight])

	// ResizeObserver: apply user resize, but never shrink below content (so 2–10 rows stay visible)
	useEffect(() => {
		const el = containerRef.current
		if (!el) return
		const ro = new ResizeObserver((entries) => {
			const h = entries[0]?.contentRect.height
			if (typeof h === 'number' && h > 0 && h >= minHeightFromContent) {
				setBoxHeight(h)
			}
		})
		ro.observe(el)
		return () => ro.disconnect()
	}, [minHeightFromContent])

	// ResizeObserver for read-only markdown: when user resizes the box, content area grows with it
	const showReadOnlyMarkdown = disabled && renderMarkdownWhenReadOnly
	const readOnlyInitialHeight = Math.max(
		readOnlyDefaultHeight,
		MIN_WRAPPER_HEIGHT,
		Math.min(
			filledHeight + CONTENT_HEIGHT_GAP,
			contentRows * ROW_HEIGHT + CONTENT_HEIGHT_GAP,
		),
	)
	useEffect(() => {
		if (!showReadOnlyMarkdown) return
		const el = containerRef.current
		if (!el) return
		const ro = new ResizeObserver((entries) => {
			const h = entries[0]?.contentRect.height
			if (typeof h === 'number' && h >= readOnlyDefaultHeight) {
				setReadOnlyBoxHeight(h)
			}
		})
		ro.observe(el)
		return () => ro.disconnect()
	}, [showReadOnlyMarkdown])

	// Handle editor mount
	const handleEditorMount: OnMount = (editor, monaco) => {
		editorRef.current = editor

		if (isSqlScript) {
			registerSqlAutocomplete(monaco)
		}

		// Add F11 keybinding for fullscreen
		editor.addCommand(monaco.KeyCode.F11, () => {
			setIsFullscreenOpen(true)
		})

		// Add Ctrl+S keybinding
		editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
			handleBlur()
		})

		// Tab / Shift+Tab: jump focus out of editor (don't insert tab); flush value before leaving
		const domNode = editor.getDomNode()
		if (domNode) {
			const onKeyDown = (e: KeyboardEvent) => {
				if (e.key !== 'Tab') return
				e.preventDefault()
				e.stopPropagation()
				const currentValue = editor.getValue()
				if (!disabled && onChange && currentValue !== value) {
					onChange(currentValue)
				}
				moveFocusToAdjacent(e.shiftKey)
			}
			domNode.addEventListener('keydown', onKeyDown, true)
			editor.onDidDispose(() => {
				domNode.removeEventListener('keydown', onKeyDown, true)
			})
		}
	}

	// Handle value changes – update local state and parent so dirty/autosave react
	const handleChange = useCallback(
		(newValue: string | undefined) => {
			const val = newValue ?? ''
			setEditorValue(val)
			onChange?.(val)
		},
		[onChange],
	)

	// Handle blur - save changes
	const handleBlur = useCallback(() => {
		if (!disabled && onChange && editorValue !== value) {
			onChange(editorValue)
		}
	}, [disabled, editorValue, onChange, value])

	// Handle fullscreen save
	const handleFullscreenSave = useCallback(
		(newValue: string) => {
			setEditorValue(newValue)
			onChange?.(newValue)
			setIsFullscreenOpen(false)
		},
		[onChange],
	)

	const displayValue = showReadOnlyMarkdown
		? (value ?? editorValue ?? '')
		: (editorValue ?? value ?? '')

	if (showReadOnlyMarkdown) {
		const heightToUse = readOnlyBoxHeight ?? readOnlyInitialHeight
		return (
			<>
				<Box
					ref={containerRef}
					sx={{
						width: '100%',
						minHeight: readOnlyDefaultHeight,
						height: heightToUse,
						resize: 'vertical',
						overflow: 'hidden',
						boxSizing: 'border-box',
						pt: 1.5,
					}}
				>
					<FormControl
						variant="outlined"
						fullWidth
						size="small"
						disabled
						sx={{
							height: '100%',
							position: 'relative',
							'& .MuiInputLabel-root': { overflow: 'visible' },
						}}
					>
						<InputLabel
							id={`monaco-readonly-${title.replace(/\s/g, '-')}`}
							shrink
						>
							{title}
						</InputLabel>
						<OutlinedInput
							label={title}
							notched
							readOnly
							inputComponent={MarkdownReadOnlyInput}
							inputProps={{ value: displayValue }}
							sx={{
								height: '100%',
								alignItems: 'stretch',
								'& .MuiOutlinedInput-input, & .MuiInputBase-input': {
									padding: '0 14px 8px 14px',
									marginTop: '5px',
									height: '100%',
									minHeight: Math.max(emptyHeight, MIN_WRAPPER_HEIGHT),
									overflow: 'auto',
									display: 'block',
									width: '100%',
									boxSizing: 'border-box',
								},
							}}
						/>
						<Tooltip title={t('FULLSCREEN_F11')}>
							<IconButton
								size="small"
								onClick={() => setIsFullscreenOpen(true)}
								aria-label={t('FULLSCREEN_F11')}
								sx={{
									position: 'absolute',
									top: 8,
									right: 8,
									zIndex: 1,
									bgcolor: 'background.paper',
									boxShadow: 1,
									'&:hover': { bgcolor: 'action.hover' },
								}}
							>
								<FullscreenIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</FormControl>
				</Box>
				<FullscreenEditorModal
					open={isFullscreenOpen}
					onClose={() => setIsFullscreenOpen(false)}
					onSave={handleFullscreenSave}
					value={displayValue}
					title={title}
					elementLabel={elementLabel}
					language={language}
					isSqlScript={isSqlScript}
					readOnly={true}
				/>
			</>
		)
	}

	return (
		<>
			<Box
				ref={containerRef}
				sx={{
					width: '100%',
					minHeight: Math.max(emptyHeight, MIN_WRAPPER_HEIGHT),
					height: boxHeight,
					resize: 'vertical',
					overflow: 'hidden',
					boxSizing: 'border-box',
					pt: 1.5,
				}}
			>
				<FormControl
					variant="outlined"
					fullWidth
					size="small"
					disabled={disabled}
					sx={{
						height: '100%',
						position: 'relative',
						'& .MuiInputLabel-root': { overflow: 'visible' },
					}}
				>
					<InputLabel id={`monaco-${title.replace(/\s/g, '-')}`} shrink>
						{title}
					</InputLabel>
					<OutlinedInput
						label={title}
						notched
						inputComponent={MonacoEditorInput}
						inputProps={{
							value: editorValue,
							onChange: handleChange,
							language,
							disabled,
							boxHeight,
							onMount: handleEditorMount,
							isSqlScript,
						}}
						sx={{
							height: '100%',
							alignItems: 'stretch',
							pr: 0,
							// Input slot and Monaco: pt so first line not hidden (MDD-2600);
							// pr so text does not run under fullscreen button (MDD-2601)
							'& .MuiOutlinedInput-input, & .MuiInputBase-input': {
								padding: 0,
								paddingTop: '6px',
								paddingRight: `${FULLSCREEN_BUTTON_RIGHT_PADDING}px`,
								height: '100%',
								minHeight: Math.max(emptyHeight, MIN_WRAPPER_HEIGHT),
								overflow: 'hidden',
								display: 'block',
								width: '100%',
								boxSizing: 'border-box',
							},
						}}
					/>
					<Tooltip title={t('FULLSCREEN_F11')}>
						<IconButton
							size="small"
							onClick={() => setIsFullscreenOpen(true)}
							aria-label={t('FULLSCREEN_F11')}
							sx={{
								position: 'absolute',
								top: 8,
								right: 8,
								zIndex: 1,
								bgcolor: 'background.paper',
								boxShadow: 1,
								'&:hover': { bgcolor: 'action.hover' },
							}}
						>
							<FullscreenIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</FormControl>
			</Box>

			<FullscreenEditorModal
				open={isFullscreenOpen}
				onClose={() => setIsFullscreenOpen(false)}
				onSave={handleFullscreenSave}
				value={editorValue}
				title={title}
				elementLabel={elementLabel}
				language={language}
				isSqlScript={isSqlScript}
				readOnly={disabled}
			/>
		</>
	)
}
